import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Leaf, Radar, FileCheck, Truck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import { IMAGES } from '../mock';

const Fleet = () => {
  const { t, lang } = useLang();

  const highlights = [
    { icon: Leaf, title: lang === 'cs' ? '100 % Euro 6' : '100% Euro 6', desc: lang === 'cs' ? 'Moderní motory splňující nejpřísnější emisní normy.' : 'Modern engines meeting the strictest emission norms.' },
    { icon: Radar, title: lang === 'cs' ? 'GPS 24/7' : 'GPS 24/7', desc: lang === 'cs' ? 'Sledování polohy v reálném čase u každého vozidla.' : 'Real-time tracking on every single vehicle.' },
    { icon: ShieldCheck, title: 'CMR', desc: lang === 'cs' ? 'Pojištění nákladu do 500 000 €.' : 'Cargo insured up to €500,000.' },
    { icon: FileCheck, title: lang === 'cs' ? 'ADR / Custom' : 'ADR / Customs', desc: lang === 'cs' ? 'ADR přeprava a celní odbavení na vyžádání.' : 'ADR transport and customs clearance on demand.' },
  ];

  return (
    <div>
      <SEO page="fleet" />
      {/* Header */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 md:pt-24 pb-14 md:pb-16">
        <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-800">
            <span className="w-6 h-px bg-red-500" />{t.fleet.eyebrow}<span className="w-6 h-px bg-red-500" />
          </div>
          <h1 className="mt-5 text-[40px] md:text-[54px] leading-[1.05] font-semibold tracking-tight text-slate-900">{t.fleet.title}</h1>
          <p className="mt-5 text-[16.5px] text-slate-600 max-w-3xl mx-auto leading-relaxed">{t.fleet.subtitle}</p>
        </div>
      </section>

      {/* Highlights */}
      <section className="pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-800 flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <div className="mt-5 text-[16.5px] font-semibold text-slate-900">{h.title}</div>
                <div className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{h.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Specs + image */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="rounded-3xl overflow-hidden ring-1 ring-slate-900/5 shadow-[0_30px_60px_-25px_rgba(15,23,42,0.3)]">
              <img src={IMAGES.fleetSunset} alt="VenaTerra fleet at dusk — tarp truck, cargo van and passenger vehicle" className="w-full h-[520px] object-cover" loading="lazy" />
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-800">
              <span className="w-6 h-px bg-red-500" />
              {lang === 'cs' ? 'Technické parametry' : 'Specifications'}
            </div>
            <h2 className="mt-4 text-[30px] md:text-[36px] leading-[1.08] font-semibold tracking-tight text-slate-900">
              {lang === 'cs' ? 'Technické parametry, které naši klienti nejčastěji ověřují.' : 'Technical parameters our clients verify most often.'}
            </h2>

            <dl className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
              {t.fleet.specs.map((spec, i) => (
                <div key={i} className="py-4 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6">
                  <dt className="text-[13.5px] text-slate-500 font-medium">{spec.label}</dt>
                  <dd className="text-[15px] text-slate-900 font-semibold sm:text-right">{spec.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button className="bg-blue-500 hover:bg-blue-800 text-white rounded-full px-6 h-12 text-[15px] font-medium">
                  {t.cta.requestQuote}
                  <ArrowRight size={17} className="ml-1.5" />
                </Button>
              </Link>
              <a href="mailto:info@venaterrasro.com?subject=Fleet%20details%20request" className="inline-flex items-center gap-1.5 h-12 px-4 rounded-full text-[14.5px] font-medium text-slate-800 hover:bg-slate-100 transition-colors">
                <Truck size={16} />
                {lang === 'cs' ? 'Vyžádat fleet list' : 'Request fleet list'}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Fleet;
